import { useEffect, useRef, useState } from 'react';
import * as T from 'three';
import { buildCityMap, isWater } from '@london-life/game/city/scene';
import { RUNWAYS, WORLD } from '@london-life/game/city/layout';
import {
  BOARD_SIZES,
  RIVER_PLOTS,
  boardName,
  boardOf,
  type CityActivity,
  type TravelMode,
} from '@london-life/contracts';
import { drawBoardPicture, drawRiverPlots } from '@london-life/game/city/props';
import { advertPicture, turnOf, type MapAdverts } from '../adverts/use-adverts';
import { createMapViewport } from '@london-life/game/scene/map-viewport';
import type { MapCommand, MapPoint } from '@london-life/game/city/types';
import { alongRoute, buildRoadGraph, findRoute, type RoadGraph } from '@london-life/game/city/routes';
import { buildVehicle } from '@london-life/game/city/traveller';
import { MAP_JET_SCALE, jetOnGround } from '@london-life/game/city/airports';
import { buildJet, type Livery } from '@london-life/game/flight/jet';
import { buildMapTrain, type TrainPaint } from '@london-life/game/city/station';
import { createSceneKit } from '@london-life/game/scene/kit';
import { AnimatedCharacter } from '@london-life/game/characters';
import type { Character } from '@london-life/game/game';
import { offerSnapshot } from '../lib/camera';

type Pin = { id: string; x: number; y: number };
// an advert board's place on the screen (tap it to see its ads)
type BoardSpot = { number: number; left: number; top: number; width: number; height: number };
// a journey being made: from one place to another by one way of travelling, between two times (ms)
export type MapJourney = {
  from: MapPoint;
  to: MapPoint;
  mode: TravelMode;
  startedAt: number;
  endsAt: number;
  character: Character;
  tag: string;
};
type Props = {
  activities: CityActivity[];
  command: { action: MapCommand; sequence: number };
  // somewhere to glide to (a place, or a spot such as the advert boards)
  focus: { id?: string; position: MapPoint } | null;
  onPlace: (place: CityActivity) => void;
  dataSaver?: boolean;
  // the places' names beside their icons ("Names" in the map's menu); icons only otherwise
  showNames?: boolean;
  // where the player is: the map opens there
  home?: MapPoint | null;
  journey?: MapJourney | null;
  // the players' ads on the boards, taking turns; tapping a board opens it
  adverts?: MapAdverts | null;
  onBoard?: (board: number) => void;
  // the game's airline: its jets wait at the airports' gates and take off and land now and then (none: no jets)
  jet?: Airline | null;
  // the lines' paint: trains wait at King's Cross's platforms and pull in and out now and then (none: no trains)
  trains?: TrainPaint[] | null;
};
type Airline = { name: string; code: string; colours: { body: string; tail: string; stripe: string } };
const liveryOf = (airline: Airline): Livery => ({ ...airline.colours, name: airline.name, code: airline.code });
// a take-off or a landing every so often, lasting this long
const JET_EVERY_MS = 45_000;
const JET_FIRST_MS = 5_000;
const JET_RETRY_MS = 8_000;
const JET_FLIGHT_MS = 14_000;
// a train in or out of King's Cross every so often, taking this long
const TRAIN_EVERY_MS = 30_000;
const TRAIN_FIRST_MS = 7_000;
const TRAIN_RUN_MS = 12_000;
// how close the camera rides along with each way of travelling (walking close up, trains from further out)
const RIDE: Record<TravelMode, number> = { walk: 42, cab: 62, car: 62, bus: 72, dlr: 90, tube: 90 };
// a walker is drawn a little larger than the buildings would make them, so they read on a phone
const WALKER = 1.6;

// render the same London map for guests and returning players
export function DistrictMap({
  activities,
  command,
  focus,
  onPlace,
  dataSaver = false,
  showNames = false,
  home = null,
  journey = null,
  adverts = null,
  onBoard,
  jet = null,
  trains = null,
}: Props) {
  const container = useRef<HTMLDivElement>(null);
  const viewport = useRef<ReturnType<typeof createMapViewport> | null>(null);
  const homeRef = useRef(home);
  homeRef.current = home;
  const focusedPlace = useRef(focus);
  focusedPlace.current = focus;
  const activitiesRef = useRef(activities);
  activitiesRef.current = activities;
  const [pins, setPins] = useState<Pin[]>([]);
  const [boardSpots, setBoardSpots] = useState<BoardSpot[]>([]);
  const names = useRef(showNames);
  names.current = showNames;
  const city = useRef<ReturnType<typeof buildCityMap> | null>(null);
  const roads = useRef<RoadGraph | null>(null);
  const journeyTag = useRef<HTMLSpanElement>(null);
  const [mode, setMode] = useState<'3d' | 'still' | 'loading'>('loading');
  const [failed, setFailed] = useState(false);

  // with Data Saver on, the map keeps its blocks (the library's models are about 1.5 MB)
  const saving = useRef(dataSaver);
  saving.current = dataSaver;
  useEffect(() => {
    viewport.current?.setDataSaver(dataSaver);
  }, [dataSaver, mode]);

  // build the floating city once
  useEffect(() => {
    const element = container.current;
    if (!element) return;
    let district: ReturnType<typeof buildCityMap> | undefined;
    let withdraw: (() => void) | undefined;
    try {
      const scene = new T.Scene();
      scene.background = new T.Color('#4f9fc3');
      scene.add(new T.HemisphereLight('#fff9ec', '#5d8aa0', 1.6));
      // a low warm sun from the south west: every building throws a clear shadow, like Lagos Life's map
      const sun = new T.DirectionalLight('#fff0d6', 3);
      district = buildCityMap(activitiesRef.current);
      city.current = district;
      scene.add(district.group);
      // the whole world: the city and the airports joined to it
      const { x, z, width, depth } = WORLD;
      const half = Math.max(width, depth) / 2 + 10;
      sun.position.set(x - 90, 160, z + 120);
      sun.target.position.set(x, 0, z);
      sun.castShadow = true;
      sun.shadow.mapSize.set(4096, 4096);
      sun.shadow.bias = -0.0006;
      sun.shadow.normalBias = 0.4;
      Object.assign(sun.shadow.camera, { left: -half, right: half, top: half, bottom: -half, near: 10, far: 520 });
      sun.shadow.camera.updateProjectionMatrix();
      scene.add(sun, sun.target);
      viewport.current = createMapViewport(
        element,
        scene,
        (camera, width, height) => {
          // keep the markers above their buildings and apart from each other
          const ordered = [...activitiesRef.current.filter((item) => item.visible)].sort(
            (a, b) => Number(b.id === focusedPlace.current?.id) - Number(a.id === focusedPlace.current?.id),
          );
          const boxes: { left: number; right: number; top: number; bottom: number }[] = [];
          const visible: Pin[] = [];
          for (const place of ordered) {
            const point = new T.Vector3(place.position[0], 6, place.position[1]).project(camera);
            const x = ((point.x + 1) * width) / 2,
              y = ((1 - point.y) * height) / 2;
            // with names on, a pin is a wide pill (the icon and the name); otherwise just the icon
            const box = { left: x - 22, right: x + (names.current ? 130 : 22), top: y - 22, bottom: y + 22 };
            const shown =
              point.z >= -1 &&
              point.z <= 1 &&
              x > 0 &&
              x < width &&
              y > 0 &&
              y < height &&
              !boxes.some(
                (other) =>
                  box.left < other.right && box.right > other.left && box.top < other.bottom && box.bottom > other.top,
              );
            if (shown) {
              boxes.push(box);
              visible.push({ id: place.id, x, y });
            }
          }
          setPins(visible);
          // the boards: where each screen is now (a tap on one opens it); too small or off the screen, none
          const spots: BoardSpot[] = [];
          const surfaces = [
            ...(district?.boards ?? []),
            ...(district ? [{ number: RIVER_PLOTS.board, corners: district.plots.corners }] : []),
          ];
          for (const board of surfaces) {
            const points = board.corners().map((corner) => corner.project(camera));
            if (points.some((point) => point.z < -1 || point.z > 1)) continue;
            const xs = points.map((point) => ((point.x + 1) * width) / 2);
            const ys = points.map((point) => ((1 - point.y) * height) / 2);
            const spot = {
              number: board.number,
              left: Math.max(0, Math.min(...xs)),
              top: Math.max(0, Math.min(...ys)),
              width: Math.min(width, Math.max(...xs)) - Math.max(0, Math.min(...xs)),
              height: Math.min(height, Math.max(...ys)) - Math.max(0, Math.min(...ys)),
            };
            if (spot.width >= 24 && spot.height >= 12) spots.push(spot);
          }
          setBoardSpots(spots);
        },
        homeRef.current ?? undefined,
      );
      setMode(viewport.current.mode);
      // the in-game camera can take the map as it is
      const map = viewport.current;
      withdraw = offerSnapshot(() => map.snapshot());
      // the library's buildings and trees arrive after the blocks: draw the map again with them
      if (viewport.current.mode === '3d' && !viewport.current.slow && !saving.current)
        void district.loadModels().then(() => viewport.current?.refresh());
    } catch (error) {
      district?.dispose();
      setFailed(true);
      console.error('London Life city map failed:', error);
    }
    return () => {
      withdraw?.();
      viewport.current?.dispose();
      viewport.current = null;
      city.current = null;
      roads.current = null;
      district?.dispose();
    };
  }, []);

  // the map opens over the player's place (known a moment after the map is up)
  const homeKey = home ? home.join(',') : '';
  useEffect(() => {
    if (home) viewport.current?.setStart(...home);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeKey]);
  // names on or off: lay the pins out again
  useEffect(() => {
    viewport.current?.draw();
  }, [showNames]);

  // a journey: the player (walking) or their bus, cab or train goes along the streets, the camera riding along,
  // their @name over them; under the river (a tunnel) they are out of sight
  const journeyKey = journey ? `${journey.from.join(',')}>${journey.to.join(',')}@${journey.startedAt}` : '';
  useEffect(() => {
    const district = city.current;
    const view = viewport.current;
    if (!journey || !district || !view || view.mode !== '3d') return;
    roads.current ??= buildRoadGraph(district.streets);
    const route = findRoute(roads.current, journey.from, journey.to);
    const holder = new T.Group();
    district.group.add(holder);
    let height = 2.4 * WALKER + 0.5;
    let walker: AnimatedCharacter | null = null;
    let vehicle: ReturnType<typeof buildVehicle> | null = null;
    if (journey.mode === 'walk') {
      // measured where it stands alone (its hair is fitted then), and only then put on the street
      const person = new AnimatedCharacter(journey.character);
      walker = person;
      person.setMotion('walk');
      void person.ready
        .then(() => {
          person.group.scale.setScalar(WALKER);
          holder.add(person.group);
        })
        .catch(() => {});
    } else {
      vehicle = buildVehicle(journey.mode);
      holder.add(vehicle.group);
      height = vehicle.height + 0.4;
    }
    const world = new T.Vector3();
    const tag = journeyTag.current;
    let frame = 0,
      last = performance.now();
    const step = () => {
      const now = performance.now();
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      const span = Math.max(1, journey.endsAt - journey.startedAt);
      const { point, heading } = alongRoute(route, (Date.now() - journey.startedAt) / span);
      holder.position.set(point[0], 0, point[1]);
      holder.rotation.y = heading;
      // under the river: in the tunnel
      holder.visible = !isWater(point[0], point[1]);
      walker?.update(dt);
      holder.getWorldPosition(world);
      view.follow(world.x, world.z, RIDE[journey.mode]);
      if (tag && element) {
        const at = world
          .clone()
          .setY(holder.visible ? height : 1)
          .project(view.camera);
        tag.style.transform = `translate(${Math.round(((at.x + 1) * element.clientWidth) / 2)}px, ${Math.round(((1 - at.y) * element.clientHeight) / 2)}px) translate(-50%, -100%)`;
        tag.style.visibility = 'visible';
      }
      frame = requestAnimationFrame(step);
    };
    const element = container.current;
    frame = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(frame);
      district.group.remove(holder);
      walker?.dispose();
      vehicle?.dispose();
      if (tag) tag.style.visibility = 'hidden';
      view.draw();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyKey, mode]);

  // the boards show the players' ads in turn (the same one for everyone at the same moment); with none booked,
  // London's own advert
  const advertsKey = adverts ? `${adverts.rotateSeconds}:${adverts.onAir.map((advert) => advert.id).join(',')}` : '';
  useEffect(() => {
    const district = city.current;
    if (!district || mode !== '3d') return;
    const showing = new Map<number, string | null>();
    let live = true;
    const turn = () => {
      for (const board of district.boards) {
        const ads = (adverts?.onAir ?? []).filter((advert) => advert.board === board.number);
        const next = ads[turnOf(ads.length, board.number, adverts?.rotateSeconds ?? 15)] ?? null;
        if ((showing.get(board.number) ?? null) === (next?.id ?? null) && showing.has(board.number)) continue;
        showing.set(board.number, next?.id ?? null);
        if (!next) {
          board.show(null);
          viewport.current?.draw();
          continue;
        }
        const { w, h } = BOARD_SIZES[boardOf(board.number)?.size ?? 'medium'];
        void advertPicture(next.image).then((image) => {
          // still its turn, and the map still here
          if (!live || showing.get(board.number) !== next.id) return image?.close();
          board.show(image ? drawBoardPicture(image, w, h) : null);
          image?.close();
          viewport.current?.draw();
        });
      }
    };
    turn();
    const timer = window.setInterval(turn, 1000);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advertsKey, mode]);

  // the river plots: each held plot shows its picture (or "SOLD" while it waits to be checked); drawn again
  // when what is held changes
  const plotsKey = adverts
    ? adverts.plots.map((plot) => `${plot.cell}:${plot.span}`).join(',') +
      '|' +
      adverts.onAir
        .filter((advert) => advert.cell !== null)
        .map((advert) => advert.id)
        .join(',')
    : '';
  useEffect(() => {
    const district = city.current;
    if (!district || mode !== '3d' || !adverts) return;
    let live = true;
    const pictures = adverts.onAir.filter((advert) => advert.board === RIVER_PLOTS.board && advert.cell !== null);
    const draw = (images: Map<number, ImageBitmap | null>) =>
      district.plots.show(
        drawRiverPlots(
          RIVER_PLOTS.columns,
          RIVER_PLOTS.rows,
          adverts.plots.map((plot) => ({ ...plot, image: images.get(plot.cell) ?? null })),
        ),
      );
    draw(new Map());
    viewport.current?.draw();
    if (pictures.length)
      void Promise.all(pictures.map(async (advert) => [advert.cell!, await advertPicture(advert.image)] as const)).then(
        (loaded) => {
          if (live) {
            draw(new Map(loaded));
            viewport.current?.draw();
          }
          for (const [, image] of loaded) image?.close();
        },
      );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plotsKey, mode]);

  // the airline's jets at the airports' gates
  const jetKey = jet ? JSON.stringify(jet) : '';
  useEffect(() => {
    const district = city.current;
    if (!district || mode !== '3d') return;
    // once the page has settled: the map and whatever opened with it come first
    const paint = () => {
      district.setLivery(jet ? liveryOf(jet) : null);
      viewport.current?.draw();
    };
    if (typeof window.requestIdleCallback === 'function') {
      const idle = window.requestIdleCallback(paint, { timeout: 4000 });
      return () => window.cancelIdleCallback(idle);
    }
    const timer = setTimeout(paint, 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, jetKey]);

  // the trains at King's Cross's platforms, in the lines' paint (once the page has settled)
  const trainKey = trains ? JSON.stringify(trains) : '';
  useEffect(() => {
    const district = city.current;
    if (!district || mode !== '3d') return;
    const paint = () => {
      district.setTrains(trains ?? []);
      viewport.current?.draw();
    };
    if (typeof window.requestIdleCallback === 'function') {
      const idle = window.requestIdleCallback(paint, { timeout: 4000 });
      return () => window.cancelIdleCallback(idle);
    }
    const timer = setTimeout(paint, 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, trainKey]);

  // now and then a train pulls out of King's Cross (speeding up along its track and off the slab's edge) or
  // comes in and slows to the buffers; only while the station is on the screen, drawn only while one moves, none
  // on data saver or without a graphics chip
  useEffect(() => {
    const district = city.current;
    const view = viewport.current;
    if (!district || !view || mode !== '3d' || dataSaver || view.slow || !trains?.length) return;
    let live = true;
    let frame = 0;
    let timer = 0;
    let running: { holder: T.Object3D; dispose: () => void } | null = null;
    const done = () => {
      if (!running) return;
      district.group.remove(running.holder);
      running.dispose();
      running = null;
      view.draw();
    };
    const next = (ms: number) => {
      window.clearTimeout(timer);
      if (live) timer = window.setTimeout(run, ms);
    };
    const spot = new T.Vector3();
    const run = () => {
      if (!live || running || document.hidden) return next(TRAIN_FIRST_MS);
      district.group.updateWorldMatrix(true, false);
      const track = district.tracks[Math.floor(Math.random() * district.tracks.length)];
      if (!track) return;
      const [x, z] = track[1]!;
      spot.set(x, 0, z).applyMatrix4(district.group.matrixWorld).project(view.camera);
      if (Math.abs(spot.x) > 1 || Math.abs(spot.y) > 1 || spot.z > 1) return next(TRAIN_FIRST_MS);
      // the way along the track: from the buffers to the edge, its length at each corner
      const legs = track.slice(1).map((point, i) => Math.hypot(point[0] - track[i]![0], point[1] - track[i]![1]));
      const total = legs.reduce((sum, item) => sum + item, 0);
      const pointAt = (distance: number) => {
        let left = Math.max(0, Math.min(total, distance));
        for (let i = 0; i < legs.length; i += 1) {
          if (left <= legs[i]! || i === legs.length - 1) {
            const [a, b] = [track[i]!, track[i + 1]!];
            const t = legs[i]! ? left / legs[i]! : 0;
            return {
              x: a[0] + (b[0] - a[0]) * t,
              z: a[1] + (b[1] - a[1]) * t,
              turn: Math.atan2(-(b[1] - a[1]), b[0] - a[0]),
            };
          }
          left -= legs[i]!;
        }
        return { x: track[0]![0], z: track[0]![1], turn: 0 };
      };
      const kit = createSceneKit();
      const train = buildMapTrain(kit, trains[Math.floor(Math.random() * trains.length)]!);
      district.group.add(train.group);
      running = { holder: train.group, dispose: kit.dispose };
      const leaving = Math.random() < 0.5;
      const started = performance.now();
      const step = () => {
        if (!live || !running) return;
        const t = Math.min(1, (performance.now() - started) / TRAIN_RUN_MS);
        // pulling out: slow then fast; coming in: fast then slowing to a stop at the platform
        const along = leaving ? t * t : 1 - (1 - t) * (1 - t);
        const distance = leaving ? along * (total + train.length) : (1 - along) * (total + train.length);
        const at = pointAt(distance + train.length / 2);
        train.group.position.set(at.x, 0.25, at.z);
        train.group.rotation.y = at.turn;
        // off the slab's edge: out of sight
        train.group.visible = distance < total + train.length / 2;
        view.draw();
        if (t < 1) frame = requestAnimationFrame(step);
        else {
          done();
          next(TRAIN_EVERY_MS);
        }
      };
      frame = requestAnimationFrame(step);
    };
    next(TRAIN_FIRST_MS);
    return () => {
      live = false;
      window.clearTimeout(timer);
      cancelAnimationFrame(frame);
      done();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, dataSaver, trainKey]);

  // jets take off and land on a runway in view now and then (Heathrow's, Gatwick's or London City's): a take-off
  // rolls faster and faster down the runway, lifts off and climbs away, its wheels folding up; a landing comes
  // down the approach, touches down and slows. Drawn only while one is moving, so the map stays still otherwise;
  // none on data saver or without a graphics chip.
  useEffect(() => {
    const district = city.current;
    const view = viewport.current;
    // not on data saver, and not without a graphics chip (drawing the whole map every frame would stall it)
    if (!district || !view || mode !== '3d' || dataSaver || view.slow || !jet) return;
    let live = true;
    let frame = 0;
    let flying: { holder: T.Object3D; dispose: () => void } | null = null;
    const land = () => {
      if (!flying) return;
      district.group.remove(flying.holder);
      flying.dispose();
      flying = null;
      view.draw();
    };
    const middle = new T.Vector3();
    // the next one: soon if no runway was in view, a while after one flew
    let timer = 0;
    const next = (ms: number) => {
      window.clearTimeout(timer);
      if (live) timer = window.setTimeout(fly, ms);
    };
    const fly = () => {
      if (!live || flying || document.hidden) return next(JET_RETRY_MS);
      // only a runway on the screen
      district.group.updateWorldMatrix(true, false);
      const seen = RUNWAYS.filter(({ from, to }) =>
        [0.1, 0.3, 0.5, 0.7, 0.9].some((t) => {
          middle
            .set(from[0] + (to[0] - from[0]) * t, 0, from[1] + (to[1] - from[1]) * t)
            .applyMatrix4(district.group.matrixWorld)
            .project(view.camera);
          return Math.abs(middle.x) < 0.9 && Math.abs(middle.y) < 0.9 && middle.z < 1;
        }),
      );
      const runway = seen[Math.floor(Math.random() * seen.length)];
      if (!runway) return next(JET_RETRY_MS);
      // either way along it
      const [[ax, az], [bx, bz]] = Math.random() < 0.5 ? [runway.from, runway.to] : [runway.to, runway.from];
      const dx = bx - ax,
        dz = bz - az;
      const takeOff = Math.random() < 0.5;
      const model = buildJet(liveryOf(jet), { detail: 'low' });
      const holder = jetOnGround(model, MAP_JET_SCALE * (runway.big ? 1 : 0.8));
      holder.rotation.y = Math.atan2(-dz, dx);
      district.group.add(holder);
      flying = { holder, dispose: model.dispose };
      const started = performance.now();
      const step = () => {
        if (!live || !flying) return;
        const seconds = (performance.now() - started) / 1000;
        const t = Math.min(1, (seconds * 1000) / JET_FLIGHT_MS);
        // how far along the runway (0 one end, 1 the other) and how high
        let along: number, height: number;
        if (takeOff) {
          along = 0.04 + t * t * 1.9;
          height = Math.max(0, along - 0.55) ** 2 * 40;
        } else {
          along = -0.85 + (1 - (1 - t) ** 2) * 1.6;
          height = Math.max(0, 0.12 - along) ** 2 * 40;
        }
        holder.position.set(ax + dx * along, height, az + dz * along);
        // nose up as it climbs away, a gentle flare as it lands
        model.group.rotation.z = height > 0 ? (takeOff ? 0.2 : 0.06) : 0;
        model.setGear(height < 6);
        model.update(seconds);
        view.draw();
        if (t < 1) frame = requestAnimationFrame(step);
        else {
          land();
          next(JET_EVERY_MS);
        }
      };
      frame = requestAnimationFrame(step);
    };
    next(JET_FIRST_MS);
    return () => {
      live = false;
      window.clearTimeout(timer);
      cancelAnimationFrame(frame);
      land();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, dataSaver, jetKey]);

  // apply zoom and place focus through one camera
  useEffect(() => {
    if (command.sequence) viewport.current?.command(command.action);
  }, [command]);
  useEffect(() => {
    if (focus) viewport.current?.focus(...focus.position);
  }, [focus]);
  useEffect(() => {
    viewport.current?.refresh();
  }, [activities]);

  return (
    <div className="district-map" aria-label="Angled game map of London Docklands and Greenwich">
      <div className="district-canvas" ref={container} />
      <div className={`district-pins${showNames ? ' show-names' : ''}`}>
        {onBoard &&
          boardSpots.map((spot) => (
            <button
              type="button"
              key={`board-${spot.number}`}
              className="district-board"
              style={{ left: spot.left, top: spot.top, width: spot.width, height: spot.height }}
              aria-label={
                spot.number === RIVER_PLOTS.board
                  ? 'River plots: see who is advertising or buy a plot'
                  : `${boardName(spot.number)}: see its ads or add yours`
              }
              onClick={() => onBoard(spot.number)}
            />
          ))}
        {pins.map((pin) => {
          // a pin whose place is missing from the content (hidden or not published) is simply not drawn
          const place = activities.find((item) => item.id === pin.id);
          if (!place) return null;
          if (place.comingSoon)
            return (
              <button
                type="button"
                key={pin.id}
                className="district-soon"
                style={{ left: pin.x, top: pin.y }}
                onClick={() => onPlace(place)}
              >
                <span className="district-pin-icon">{place.icon}</span>
                <strong className="district-pin-name">{place.name} · Coming soon</strong>
              </button>
            );
          return (
            <button
              type="button"
              key={pin.id}
              className="district-pin"
              style={{ left: pin.x, top: pin.y }}
              aria-label={`Open ${place.name}`}
              onClick={() => onPlace(place)}
            >
              <span className="district-pin-icon">{place.icon}</span>
              <strong className="district-pin-name">{place.name}</strong>
            </button>
          );
        })}
        {journey && (
          <span className="district-journey-tag" ref={journeyTag}>
            {journey.tag}
          </span>
        )}
      </div>
      {mode === 'loading' && !failed && (
        <div className="district-loading" role="status">
          Opening London…
        </div>
      )}
      {failed && (
        <div className="district-loading" role="alert">
          The city map could not load. Please refresh to try again.
        </div>
      )}
      {mode === 'still' && <div className="district-credit">Static view · 3D unavailable on this device</div>}
    </div>
  );
}
